import { Button } from "@coolset/cs-ui";
import { Dialog, DialogActions, InputAdornment, TextField, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { BlockIcon } from "shared/components/BlockOption/BlockIcon";
import { validate } from "shared/helpers/validate";
import { BlockTypes, FieldIdPrefixes, getBlockFullType, getBlockOption } from "../../helpers/blocks";
import { BlockToAdd } from "../../hooks/useMainSurvey";
import { SurveyBlocks } from "../../surveyBlocks";

export const BlockRequiredDialog = (props: BlockRequiredDialogProps) => {
  const { onSuccess, onCancel, blockToAdd, inNotFieldId } = props;

  const [fieldId, setFieldId] = useState<string>("");
  const [title, setTitle] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState(false);

  const handleAddBlock = useCallback(async () => {
    setLoading(true);
    try {
      const error = await onSuccess(fieldId, title);
      setError(error || "");

      if (!error) {
        setFieldId("");
        setTitle("");
      }
    } finally {
      setLoading(false);
    }
  }, [fieldId, onSuccess, title]);

  const canAdd = (!!fieldId || inNotFieldId) && !!title && !error;

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && canAdd && !loading) {
        e.preventDefault();
        handleAddBlock();
      }
      if (e.key === "Escape") {
        e.preventDefault();
        onCancel();
      }
    },
    [canAdd, loading, handleAddBlock, onCancel],
  );

  const fieldIdHelperText = useMemo<string>(() => error || validate.getFieldIdError(fieldId, { validateEmpty: false }) || "", [error, fieldId]);

  useEffect(() => {
    setError(validate.getFieldIdError(fieldId, { validateEmpty: false }) || "");
  }, [fieldId]);

  if (!blockToAdd) {
    return null;
  }

  const blockFullType = getBlockFullType(blockToAdd.type, blockToAdd.subType);
  const blockOption = getBlockOption(blockFullType);

  return (
    <Dialog open onKeyDown={handleKeyDown} PaperProps={{ className: "layout-block-required-dialog" }}>
      <div className="_d-grid-md-gap">
        <div className="_d-flex-ali-center-sm-gap">
          <BlockIcon blockType={blockFullType} />
          <Typography variant="h4">
            New {blockToAdd.type === BlockTypes.Survey && "nested"} {blockOption.name.toLowerCase()}
          </Typography>
        </div>
        {!inNotFieldId && (
          <TextField
            value={fieldId}
            onChange={(e) => setFieldId(e.target.value)}
            placeholder="Add an field ID to this block (required)"
            helperText={fieldIdHelperText}
            error={!!fieldIdHelperText}
            size="small"
            autoFocus
            inputProps={{ maxLength: SurveyBlocks.BlockContentCharLimit.fieldId - (FieldIdPrefixes[blockToAdd.type]?.length + 1) }}
            InputProps={{
              startAdornment: FieldIdPrefixes[blockToAdd.type] ? (
                <InputAdornment position="start">{FieldIdPrefixes[blockToAdd.type]}_</InputAdornment>
              ) : null,
            }}
          />
        )}
        <TextField
          autoFocus={inNotFieldId}
          value={title}
          inputProps={{ maxLength: SurveyBlocks.BlockContentCharLimit.title }}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Add a title to this block (required)"
          size="small"
        />
      </div>

      <DialogActions sx={{ mt: 2 }}>
        <Button onClick={onCancel} color="secondary">
          Cancel
        </Button>
        <Button onClick={handleAddBlock} disabled={!canAdd} loading={loading}>
          Add block
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface BlockRequiredDialogProps {
  blockToAdd: BlockToAdd | null;
  onSuccess: (fieldId: string, title: string) => Promise<string | undefined>;
  onCancel: () => void;
  inNotFieldId?: boolean;
}
